
import React from 'react';
import { Home, Building, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';

export const PlanTypesSection = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-white to-emergency-50">
      <div className="container px-4 md:px-6">
        <div className="text-center max-w-[800px] mx-auto mb-16">
          <h2 className="heading-xl mb-4 text-emergency-700">Find the Plan That's Right for You</h2>
          <p className="body-base text-muted-foreground max-w-[650px] mx-auto">
            Emergencies don't discriminate. They can impact individuals, families, and organizations of any size. 
            That's why we offer tailored emergency preparedness plans designed to meet your specific needs.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Residential + Family Plan Card */}
          <div className="flex flex-col">
            <Card className="border-2 border-emergency-100 hover:border-emergency-200 transition-all duration-300 shadow-sm hover:shadow-md overflow-hidden w-full" style={{ height: "543px" }}>
              <div className="bg-emergency-50 p-5 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                    <Home className="text-emergency-600 w-6 h-6" />
                  </div>
                  <CardTitle className="text-emergency-700">Residential + Family Plans</CardTitle>
                </div>
                <div className="bg-emergency-600 text-white font-medium text-sm px-4 py-2 rounded-full shadow-sm">
                  $29.99
                </div>
              </div>
              
              <CardHeader className="pt-6">
                <CardTitle className="text-xl">Your Family's Safety, Simplified</CardTitle>
                <CardDescription>Protect What Matters Most</CardDescription>
              </CardHeader>
              
              <CardContent className="flex flex-col h-full">
                <p className="text-muted-foreground mb-6">
                  Your family's safety is your top priority. Our Residential + Family Plans are designed to help simplify 
                  emergency preparedness, providing you with a clear, actionable plan in just 15 minutes.
                </p>
                
                <ul className="space-y-3 mb-8">
                  {[
                    "Specific considerations for children, seniors, and pets",
                    "Risk assessments related to your location",
                    "Step-by-step guidance for various scenarios",
                    "Useful resources and contact information"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="text-emergency-500 w-5 h-5 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-auto">
                  <a href="https://auth.myqoop.com/purchase/residential" target="_blank" rel="noopener noreferrer" className="w-full">
                    <Button variant="default" className="w-full bg-emergency-600 text-white hover:bg-emergency-700">
                      Get Your Plan Now
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
            {/* Empty div to match the height of the organization info below */}
            <div className="h-20 invisible"></div>
          </div>
          
          {/* Business + Organization Plan Card */}
          <div className="flex flex-col">
            <Card className="border-2 border-emergency-100 hover:border-emergency-200 transition-all duration-300 shadow-sm hover:shadow-md overflow-hidden w-full" style={{ height: "543px" }}>
              <div className="bg-emergency-50 p-5 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                    <Building className="text-emergency-600 w-6 h-6" />
                  </div>
                  <CardTitle className="text-emergency-700">Business + Organization Plans</CardTitle>
                </div>
                <div className="bg-emergency-600 text-white font-medium text-sm px-4 py-2 rounded-full shadow-sm">
                  $49.99
                </div>
              </div>
              
              <CardHeader className="pt-6">
                <CardTitle className="text-xl">Preparedness for Organizations</CardTitle>
                <CardDescription>Ensure Safety and Continuity</CardDescription>
              </CardHeader>
              
              <CardContent className="flex flex-col h-full">
                <p className="text-muted-foreground mb-6">
                  Perfect for businesses, churches, community groups, associations, and organizations 
                  of all types. Our Business + Organization Plans provide helpful solutions to assist in preparing 
                  for and navigating through challenging situations.
                </p>
                
                <ul className="space-y-3 mb-8">
                  {[
                    "Plans for organizational continuity",
                    "Tools for communication coordination",
                    "Approaches for minimizing disruption",
                    "Scalable solutions for all organization types"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="text-emergency-500 w-5 h-5 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-auto">
                  <a href="https://auth.myqoop.com/purchase/business" target="_blank" rel="noopener noreferrer" className="w-full">
                    <Button variant="default" className="w-full bg-emergency-600 text-white hover:bg-emergency-700">
                      Get Your Plan Now
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
            
            {/* Organization suitability note - kept under the card */}
            <div className="mt-4 p-4 bg-emergency-50 rounded-lg border border-emergency-100">
              <p className="text-sm">
                <span className="font-medium">Suitable for organizations up to 200 people</span> at a single location. 
                For larger organizations or multiple locations, please contact our sales team for a customized quote.
              </p>
              <a 
                href="mailto:support@myqoop.com" 
                className="mt-2 inline-flex items-center text-emergency-700 hover:text-emergency-800 font-medium text-sm"
              >
                <span className="inline-flex items-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1">
                    <path d="M3 7.5C3 6.67157 3.67157 6 4.5 6H19.5C20.3284 6 21 6.67157 21 7.5V16.5C21 17.3284 20.3284 18 19.5 18H4.5C3.67157 18 3 17.3284 3 16.5V7.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 7.5L12 13.5L21 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Contact Sales
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlanTypesSection;
